import { makeAuthenticatedRequest } from "./authApi.js";
import { getApiUrl } from "../utils/envConfig";
import { getAuthToken } from "../utils/authUtils";
import { resolveDefaultWorkspace } from "../utils/defaultWorkspace";

export const fetchAllIdClients = async () => {
  try {
    const url = `${getApiUrl()}/distinct_id_client`;
    const response = await makeAuthenticatedRequest(url);
    return response.id_client || [];
  } catch (error) {
    throw new Error("Failing fetching all client ids: " + error.message);
  }
};

export const fetchAllMarkets = async ({ id_client }) => {
  try {
    const url = `${getApiUrl()}/client/${encodeURIComponent(id_client)}/countries`;
    const countries = await makeAuthenticatedRequest(url);
    return Array.isArray(countries) ? countries : [];
  } catch (error) {
    throw new Error("Failing fetching all markets: " + error.message);
  }
};

// Runtime CASS identity graph: the durable, daily-synced replacement for the
// static cassAccountMapping.js. Served by the main FastAPI (VITE_API_BASE_URL),
// NOT the CASS backend. Returns { workspace_to_cass_accounts, workspace_to_id_client,
// geoscope_configured, updated_at }. Pass forceRefresh to bypass the 24h server cache.
export const fetchCassIdMapping = async ({ forceRefresh = false } = {}) => {
  try {
    const query = forceRefresh ? "?force_refresh=true" : "";
    const url = `${getApiUrl()}/cass/id-mapping${query}`;
    const data = await makeAuthenticatedRequest(url);
    return {
      workspace_to_cass_accounts: data?.workspace_to_cass_accounts || {},
      workspace_to_id_client: data?.workspace_to_id_client || {},
      // null (lookup failed / older API) means fail open: no dropdown filter.
      geoscope_configured: data?.geoscope_configured ?? null,
      updated_at: data?.updated_at || "",
    };
  } catch (error) {
    throw new Error("Failing fetching CASS id mapping: " + error.message);
  }
};

/**
 * Distinct refresh periods (YYYYMM) the client has data for.
 *
 * `GET /client/{id_client}/refresh` answers 404 when the client has no rows in
 * `dws_outlet_refresh_summary` — the normal case for a client whose first
 * workspace this is. That is a legitimate empty result, not a failure, so the
 * status is preserved: callers render "no refresh periods for this client" on
 * 404 and a retryable error otherwise.
 */
export const fetchRefresh = async ({ id_client }) => {
  try {
    const url = `${getApiUrl()}/client/${encodeURIComponent(id_client)}/refresh`;
    const refreshData = await makeAuthenticatedRequest(url);
    return refreshData;
  } catch (error) {
    const err = new Error("Failing fetching refresh data: " + error.message);
    err.status = error.status;
    throw err;
  }
};

export const fetchSKUCompanies = async ({ id_client, country }) => {
  try {
    const url = `${getApiUrl()}/client/${encodeURIComponent(id_client)}/country/${encodeURIComponent(country)}/brands/companies`;
    const companiesData = await makeAuthenticatedRequest(url);
    return companiesData;
  } catch (error) {
    throw new Error(
      "Failing fetching internal tool companies: " + error.message
    );
  }
};

export const fetchCompanyAllBrands = async ({
  id_client,
  country,
  company,
}) => {
  try {
    const url = `${getApiUrl()}/client/${encodeURIComponent(id_client)}/country/${encodeURIComponent(country)}/brands/tree/${encodeURIComponent(company)}`;
    const brandsData = await makeAuthenticatedRequest(url);
    return brandsData;
  } catch (error) {
    throw new Error("Failing fetching internal tool brands: " + error.message);
  }
};

export const fetchCompaniesWithBrands = async ({ id_client, country }) => {
  try {
    const url = `${getApiUrl()}/client/${encodeURIComponent(id_client)}/country/${encodeURIComponent(country)}/brands/tree`;
    const companiesBrandsData = await makeAuthenticatedRequest(url);
    return companiesBrandsData;
  } catch (error) {
    console.error(`🌐 fetchCompaniesWithBrands API error:`, error);
    throw new Error(
      "Failing fetching internal tool companies with brands: " + error.message
    );
  }
};
export const createWorkspace = async ({ id_client, workspace_info }) => {
  try {
    const url = `${getApiUrl()}/${encodeURIComponent(id_client)}/pet_formula/default`;
    const createWorkspaceData = await makeAuthenticatedRequest(url, {
      method: "POST",
      body: JSON.stringify(workspace_info),
    });

    return createWorkspaceData;
  } catch (error) {
    console.error(`🌐 createWorkspace API error:`, error);
    const err = new Error("Failing createWorkspace: " + error.message);
    // Preserved so Create Workspace can tell a server rejection (status
    // present, e.g. the 409 on an existing workspace) from an unknown outcome
    // (network drop, status absent) — it only undoes its earlier geoscope
    // append in the former case.
    err.status = error.status;
    throw err;
  }
};

export const getAllWorkspaces = async () => {
  try {
    const url = `${getApiUrl()}/all_clients/formula_weights`;
    const response = await makeAuthenticatedRequest(url);

    // The API now returns { data: [...], total_clients: number, total_records: number }
    // Return the structured response as-is so components can access both data and metadata
    return {
      workspaces: response.data || [],
      totalClients: response.total_clients || 0,
      totalRecords: response.total_records || 0,
      // Keep raw response for backward compatibility if needed
      raw: response,
    };
  } catch (error) {
    console.error(`🌐 getAllWorkspaces API error:`, error);
    throw new Error("Failing getAllWorkspaces: " + error.message);
  }
};

/**
 * Every workspace's invite domain allowlist, in one read.
 *
 * GET /admin/workspaces/allowed-domains — Dashmote-admin only. Returns
 * `[{ id_client, allowed_domains }]`; workspaces with no entitlements doc are
 * absent (render them as "Not configured"). Powers the Allowed Domains column.
 */
export const getAllowedDomains = async () => {
  try {
    const url = `${getApiUrl()}/admin/workspaces/allowed-domains`;
    const data = await makeAuthenticatedRequest(url);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error(`🌐 getAllowedDomains API error:`, error);
    throw new Error("Failing getAllowedDomains: " + error.message);
  }
};

/**
 * Replace one workspace's invite domain allowlist.
 *
 * PUT /admin/workspaces/{id_client}/allowed-domains — Dashmote-admin only,
 * audited server-side. An empty list is a valid write (= unrestricted). Returns
 * the stored (normalized) `{ id_client, allowed_domains }`.
 *
 * @param {object} args
 * @param {string} args.id_client - Workspace to configure (the workspace name).
 * @param {string[]} args.allowed_domains - Domains that may be invited.
 */
export const setAllowedDomains = async ({ id_client, allowed_domains }) => {
  try {
    const url = `${getApiUrl()}/admin/workspaces/${encodeURIComponent(
      id_client,
    )}/allowed-domains`;
    const response = await makeAuthenticatedRequest(url, {
      method: "PUT",
      body: JSON.stringify({ allowed_domains }),
    });
    return response;
  } catch (error) {
    console.error(`🌐 setAllowedDomains API error:`, error);
    const err = new Error("Failing setAllowedDomains: " + error.message);
    err.status = error.status;
    throw err;
  }
};

/**
 * One workspace's combo names.
 *
 * GET /admin/workspaces/{id_client}/combo-names — Dashmote-admin only. Returns
 * `{ id_client, key_accounts_fixed, key_accounts_bundle, key_accounts_option,
 * fixed_combo, option_combo }`, with null for a name the workspace has not set.
 *
 * @param {string} idClient - Workspace to read.
 */
export const getComboNames = async (idClient) => {
  try {
    const url = `${getApiUrl()}/admin/workspaces/${encodeURIComponent(
      idClient,
    )}/combo-names`;
    return await makeAuthenticatedRequest(url);
  } catch (error) {
    console.error(`🌐 getComboNames API error:`, error);
    throw new Error("Failing getComboNames: " + error.message);
  }
};

/**
 * Replace one workspace's combo names: the three Key Accounts names and the
 * fixed and option combo names the other pages show.
 *
 * PUT /admin/workspaces/{id_client}/combo-names — Dashmote-admin only, audited
 * server-side. The server trims each name, refuses one over 40 characters or
 * with a control character, and removes the key for a blank one. It keeps
 * `combo_metric.basis` and every other label. Returns the stored names.
 *
 * @param {object} args
 * @param {string} args.id_client - Workspace to configure.
 * @param {string} args.key_accounts_fixed - Fixed combo name, or "".
 * @param {string} args.key_accounts_bundle - Bundle add name, or "".
 * @param {string} args.key_accounts_option - Upsell name, or "".
 * @param {string} args.fixed_combo - Fixed combo name for the other pages, or "".
 * @param {string} args.option_combo - Option combo name for the other pages, or "".
 */
export const setComboNames = async ({ id_client, ...names }) => {
  try {
    const url = `${getApiUrl()}/admin/workspaces/${encodeURIComponent(
      id_client,
    )}/combo-names`;
    return await makeAuthenticatedRequest(url, {
      method: "PUT",
      body: JSON.stringify({
        key_accounts_fixed: names.key_accounts_fixed ?? "",
        key_accounts_bundle: names.key_accounts_bundle ?? "",
        key_accounts_option: names.key_accounts_option ?? "",
        fixed_combo: names.fixed_combo ?? "",
        option_combo: names.option_combo ?? "",
      }),
    });
  } catch (error) {
    console.error(`🌐 setComboNames API error:`, error);
    const err = new Error("Failing setComboNames: " + error.message);
    err.status = error.status;
    throw err;
  }
};

/**
 * One workspace's Key Accounts SKU groups.
 *
 * GET /admin/workspaces/{id_client}/key-account-skus — Dashmote-admin only.
 * Returns `{ id_client, groups: [{ label, subbrands, rest }] }`, the groups the
 * page uses, in order.
 *
 * @param {string} idClient - Workspace to read.
 */
export const getKeyAccountSkus = async (idClient) => {
  try {
    const url = `${getApiUrl()}/admin/workspaces/${encodeURIComponent(
      idClient,
    )}/key-account-skus`;
    return await makeAuthenticatedRequest(url);
  } catch (error) {
    console.error(`🌐 getKeyAccountSkus API error:`, error);
    throw new Error("Failing getKeyAccountSkus: " + error.message);
  }
};

/**
 * Replace one workspace's Key Accounts SKU groups whole.
 *
 * PUT /admin/workspaces/{id_client}/key-account-skus — Dashmote-admin only,
 * audited server-side. The server trims each label and key, caps the lists
 * as the page does, and removes the setting for an empty list. Returns the
 * stored groups.
 *
 * @param {object} args
 * @param {string} args.id_client - Workspace to configure.
 * @param {{label: string, subbrands: string[], rest: boolean}[]} args.groups
 */
export const setKeyAccountSkus = async ({ id_client, groups }) => {
  try {
    const url = `${getApiUrl()}/admin/workspaces/${encodeURIComponent(
      id_client,
    )}/key-account-skus`;
    return await makeAuthenticatedRequest(url, {
      method: "PUT",
      body: JSON.stringify({ groups: groups ?? [] }),
    });
  } catch (error) {
    console.error(`🌐 setKeyAccountSkus API error:`, error);
    const err = new Error("Failing setKeyAccountSkus: " + error.message);
    err.status = error.status;
    throw err;
  }
};

/**
 * A page of audit events — GET /audit-events, Dashmote-admin only.
 *
 * At most ONE filter (subject_email | subject_uid | event_type) per the API
 * (it 400s on more than one). Cursor-paginated: pass the previous page's
 * `nextCursor` to continue.
 *
 * @param {object} [args]
 * @returns {Promise<{events: object[], nextCursor: string|null}>}
 */
export const getAuditEvents = async ({
  subject_email,
  subject_uid,
  event_type,
  limit = 50,
  cursor,
} = {}) => {
  try {
    const params = new URLSearchParams();
    if (subject_email) params.set("subject_email", subject_email);
    if (subject_uid) params.set("subject_uid", subject_uid);
    if (event_type) params.set("event_type", event_type);
    if (limit) params.set("limit", String(limit));
    if (cursor) params.set("cursor", cursor);
    const url = `${getApiUrl()}/audit-events?${params.toString()}`;
    const data = await makeAuthenticatedRequest(url);
    return { events: data?.events || [], nextCursor: data?.next_cursor || null };
  } catch (error) {
    console.error(`🌐 getAuditEvents API error:`, error);
    throw new Error("Failing getAuditEvents: " + error.message);
  }
};

/**
 * Add or update one or more markets (countries) on an EXISTING workspace.
 *
 * Calls `PUT /{id_client}/pet_formula/update`, which creates a new active
 * master version: new countries are added with an initial version, existing
 * countries get their version incremented, untouched countries are preserved.
 * The backend triggers the data pipeline asynchronously, so the response
 * returns before processing completes.
 *
 * @param {object} args
 * @param {string} args.id_client - The workspace identifier (the workspace name).
 * @param {object} args.formula_update - Request body: `{ countries: [...],
 *   comment, default_id_client }`. Each country carries its weights (decimals
 *   summing to 1.0), core_brands, companies, and competitors.
 */
export const updatePetFormula = async ({ id_client, formula_update }) => {
  try {
    const url = `${getApiUrl()}/${encodeURIComponent(id_client)}/pet_formula/update`;
    const response = await makeAuthenticatedRequest(url, {
      method: "PUT",
      body: JSON.stringify(formula_update),
    });
    return response;
  } catch (error) {
    console.error(`🌐 updatePetFormula API error:`, error);
    const err = new Error("Failing updatePetFormula: " + error.message);
    // Preserved so Add Market can tell a server rejection (status present)
    // from an unknown outcome (network drop, status absent) — it only undoes
    // its earlier geoscope write in the former case.
    err.status = error.status;
    throw err;
  }
};

/**
 * Poll the pipeline status of a pet_formula version after a save.
 * Returns { version_id, status: 'pending'|'done'|'failed', dag_run_id,
 * dag_run_url, error_message }. Both dag_run_* names are legacy: they carry the
 * Databricks run id and its run-page URL. dag_run_url stays null until the
 * background trigger records the run id.
 */
export const getPetFormulaVersionStatus = async ({ id_client, version_id }) => {
  try {
    const url = `${getApiUrl()}/${encodeURIComponent(id_client)}/pet_formula/version/${encodeURIComponent(version_id)}/pipeline-status`;
    return await makeAuthenticatedRequest(url);
  } catch (error) {
    console.error(`🌐 getPetFormulaVersionStatus API error:`, error);
    throw new Error("Failing getPetFormulaVersionStatus: " + error.message);
  }
};

// Full client-level geoscope tree (admin, no per-user filter) — a workspace's
// own business market scope. Used by the CASS Explorer Workspace view so each
// workspace shows ITS markets: workspaces that share a DWD id_client still have
// distinct geoscopes, so this is keyed by the workspace id_client, not the DWD one.
// retries=0: "not configured" is a deterministic 404 (typed by the backend),
// so retries buy nothing there and only stall the scope bar on real outages.
// err.status is preserved so the caller can tell "not configured" (404 -> an
// honest empty state) from a real failure (error state).
export const fetchClientGeoscope = async ({ id_client }) => {
  try {
    const url = `${getApiUrl()}/${encodeURIComponent(id_client)}/geoscope/`;
    const data = await makeAuthenticatedRequest(url, {}, 0);
    return Array.isArray(data) ? data : [];
  } catch (error) {
    const err = new Error(
      "Failing fetching client geoscope: " + error.message,
    );
    err.status = error.status;
    throw err;
  }
};

// Append markets to a workspace's geoscope regions, creating the region (or
// the whole document) when absent. It uses the append endpoint rather than the
// create-only `POST /{id_client}/geoscope/`, which 409s against any workspace
// that is already configured.
//
// The error is rethrown as-is rather than re-wrapped: makeAuthenticatedRequest
// already surfaces FastAPI's `detail` as the message and attaches `status` on
// a server response, and Add Market's rollback keys on that status to tell a
// real rejection from an unknown outcome (a network drop leaves status unset).
export const appendGeoscopeMarkets = async ({ id_client, regions_info }) => {
  try {
    const url = `${getApiUrl()}/${encodeURIComponent(id_client)}/geoscope/markets`;
    return await makeAuthenticatedRequest(url, {
      method: "POST",
      body: JSON.stringify(regions_info),
    });
  } catch (error) {
    console.error(`🌐 appendGeoscopeMarkets API error:`, error);
    throw error;
  }
};

// Remove specific country codes from one geoscope region — the compensating
// counterpart of appendGeoscopeMarkets, used to undo an append when a later
// write in the same flow fails. Idempotent.
export const removeGeoscopeMarkets = async ({
  id_client,
  region,
  country_codes,
}) => {
  try {
    const params = new URLSearchParams();
    (country_codes || []).forEach((code) =>
      params.append("country_codes", code),
    );
    const url =
      `${getApiUrl()}/${encodeURIComponent(id_client)}` +
      `/geoscope/${encodeURIComponent(region)}/markets?${params.toString()}`;
    return await makeAuthenticatedRequest(url, { method: "DELETE" });
  } catch (error) {
    console.error(`🌐 removeGeoscopeMarkets API error:`, error);
    throw error;
  }
};

// Delete a whole geoscope region. Used as the rollback when a submit created
// the region itself, where taking back only its markets would leave an empty
// region behind.
export const deleteGeoscopeRegion = async ({ id_client, region }) => {
  try {
    const url =
      `${getApiUrl()}/${encodeURIComponent(id_client)}` +
      `/geoscope/${encodeURIComponent(region)}`;
    return await makeAuthenticatedRequest(url, { method: "DELETE" });
  } catch (error) {
    console.error(`🌐 deleteGeoscopeRegion API error:`, error);
    throw error;
  }
};

export const inviteUsersToWorkspaces = async ({
  emails,
  workspaceAssignments,
  accountType = null,
  defaultWorkspaceId = null,
}) => {
  try {
    const url = `${getApiUrl()}/users/invite`;
    const results = [];

    // Every invited email shares one workspace assignment set, so the default
    // workspace is per-invite rather than per-email. Resolve it once.
    const defaultIdClient = resolveDefaultWorkspace(
      workspaceAssignments,
      defaultWorkspaceId
    );

    // Process each email separately with multiple API calls
    for (const emailObj of emails) {
      const userData = {
        email: emailObj.email,
        name: emailObj.name && emailObj.name.trim() !== "" ? emailObj.name.trim() : null,
        default_id_client: defaultIdClient,
        id_client: {},
      };

      // Staff-only account classification (backend gates to Dashmote admins).
      if (accountType) {
        userData.account_type = accountType;
      }

      // Process each workspace assignment for this user
      workspaceAssignments.forEach((assignment) => {
        const workspaceId = assignment.workspaceId;
        const role = assignment.role;

        if (role === "super_admin") {
          // Super Admin structure - no product_scopes needed
          userData.id_client[workspaceId] = {
            role: "super_admin",
            // is_super_admin will be set automatically to true by backend
          };
        } else if (role === "admin") {
          // Admin structure - per-product market scope, all products at once
          // (the scope picker collapses Admin to a single "All Products" row).
          userData.id_client[workspaceId] = {
            role: "admin",
            product_scopes: assignment.product_scopes,
          };
        } else {
          // Normal User structure - per-product market scope from the modal
          // (matches User Scope Management).
          userData.id_client[workspaceId] = {
            role: "normal_user",
            // is_super_admin will be set automatically to false by backend
            product_scopes: assignment.product_scopes,
          };
        }
      });

      if (import.meta.env.DEV) {
        console.log(
          `🚀 Inviting user ${emailObj.email} with structure:`,
          userData
        );
      }

      // Make individual API call for each user
      const result = await makeAuthenticatedRequest(url, {
        method: "POST",
        body: JSON.stringify(userData),
      });

      results.push({
        email: emailObj.email,
        success: true,
        result: result,
      });
    }

    if (import.meta.env.DEV) console.log("🚀 All users invited successfully:", results);
    return results;
  } catch (error) {
    console.error(`🌐 Invite users API error:`, error);
    // Re-throw the original error so the backend's reason (error.message, set
    // from FastAPI's `detail` by makeAuthenticatedRequest) and `error.status`
    // reach the caller's catch block. Wrapping it in a new Error with a
    // "Failing inviteUsersToWorkspaces: " prefix would bury the real reason.
    throw error;
  }
};

export const getAllUsersInfo = async () => {
  try {
    const url = `${getApiUrl()}/all-users?exclude_dashmote_emails=false`;
    const allUsersData = await makeAuthenticatedRequest(url);
    return allUsersData;
  } catch (error) {
    throw new Error("Failing fetching all users: " + error.message);
  }
};

export const updateUserInfo = async ({ email, user_info }) => {
  try {
    const url = `${getApiUrl()}/users/email/${encodeURIComponent(email)}`;
    const updateUserInfoData = await makeAuthenticatedRequest(url, {
      method: "PUT",
      body: JSON.stringify(user_info),
    });

    return updateUserInfoData;
  } catch (error) {
    console.error(`🌐 Update User Info API error:`, error);
    throw new Error("Failing update user info: " + error.message);
  }
};

// Map the modal's workspace-assignment rows to the backend user payload
// (email + name + default_id_client + per-workspace id_client roles/scopes).
// Shared by createUserByEmail and the reactivate-then-apply-scope recovery
// so both produce an identical id_client structure.
// `defaultWorkspaceId` is the admin's explicit pick. Omitting it lands on the
// first assigned workspace.
export const buildUserClientPayload = ({
  email,
  name,
  workspaceAssignments,
  accountType = null,
  defaultWorkspaceId = null,
}) => {
  const defaultIdClient = resolveDefaultWorkspace(
    workspaceAssignments,
    defaultWorkspaceId
  );

  const userData = {
    email: email,
    name: name,
    default_id_client: defaultIdClient,
    id_client: {},
  };

  // Staff-only account classification (backend gates to Dashmote admins).
  if (accountType) {
    userData.account_type = accountType;
  }

  workspaceAssignments.forEach((assignment) => {
    const workspaceId = assignment.workspaceId;
    const role = assignment.role;

    if (role === "super_admin") {
      userData.id_client[workspaceId] = {
        role: "super_admin",
      };
    } else if (role === "admin") {
      userData.id_client[workspaceId] = {
        role: "admin",
        product_scopes: assignment.product_scopes,
      };
    } else {
      userData.id_client[workspaceId] = {
        role: "normal_user",
        product_scopes: assignment.product_scopes,
      };
    }
  });

  return userData;
};

export const createUserByEmail = async ({
  email,
  name,
  workspaceAssignments,
  accountType = null,
  defaultWorkspaceId = null,
}) => {
  try {
    const url = `${getApiUrl()}/users/email`;

    const userData = buildUserClientPayload({
      email,
      name,
      workspaceAssignments,
      accountType,
      defaultWorkspaceId,
    });

    if (import.meta.env.DEV) console.log("🚀 Creating user with structure:", userData);

    const result = await makeAuthenticatedRequest(url, {
      method: "POST",
      body: JSON.stringify(userData),
    });

    if (import.meta.env.DEV) console.log("🚀 User created successfully:", result);
    return result;
  } catch (error) {
    console.error(`🌐 Create user by email API error:`, error);
    // Re-throw the backend's own message + status unchanged so the UI shows the
    // real reason (e.g. "…is deactivated") and can detect 409 for the reactivate
    // offer. A "Failing createUserByEmail:" prefix would bury both.
    const wrapped = new Error(error.message);
    if (error.status !== undefined) wrapped.status = error.status;
    throw wrapped;
  }
};

/**
 * Resend an invitation link for a user whose original activation link expired
 * or was lost.
 *
 * Calls the **authenticated** `POST /users/admin/resend-invitation` endpoint
 * alongside the invitation-lifecycle audit log. The endpoint records the
 * caller's identity from the Firebase token —
 * audit `actor.type = "dashmote_staff"` when the caller's email ends in
 * `@dashmote.com`, else `"workspace_admin"`. Without the Bearer token the
 * resend routes through the public anonymous endpoint and loses staff
 * attribution.
 *
 * Bare `fetch` + manually-attached Bearer token rather than
 * `makeAuthenticatedRequest`, which throws a plain `Error` on 4xx and strips
 * `error.status` — and `handleResendInvitation` in `UserManagementPage.jsx`
 * branches on `error?.status === 429` to display the rate-limit-specific
 * message. Same shape as the dashone-web sibling implementation.
 *
 * The endpoint always returns a generic 200 (enumeration defence — same shape
 * for pending / activated / nonexistent emails), so callers should never
 * branch on response content. The only failure paths the caller needs to
 * handle are HTTP 429 (rate-limited: 3 per email per 5 minutes) and HTTP 403
 * (caller lacks workspace-admin or dashmote-staff role).
 *
 * @param {object} args
 * @param {string} args.email - Email address to send the new invitation to.
 * @returns {Promise<{success: boolean, message: string}>}
 * @throws {Error} with `.status` attached on non-OK responses.
 */
export const resendInvitation = async ({ email }) => {
  try {
    const token = await getAuthToken();
    const url = `${getApiUrl()}/users/admin/resend-invitation`;
    const res = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ email }),
    });
    if (!res.ok) {
      let detail = "";
      try {
        detail = (await res.json())?.detail || "";
      } catch {
        // body wasn't JSON; fall through to the generic message
      }
      const err = new Error(
        detail || `Failed to resend invitation (HTTP ${res.status}).`,
      );
      err.status = res.status;
      throw err;
    }
    return res.json();
  } catch (error) {
    console.error("🌐 resendInvitation API error:", error);
    throw error;
  }
};

// Read-only preview of what deleting a workspace would remove. The operator
// script dry-runs by default; the modal's confirmation executes immediately, so
// this is what lets the operator see the actual volume — per-collection counts
// plus how many accounts lose access — before confirming.
export const previewWorkspaceDeletion = async (id_client) => {
  try {
    const url = `${getApiUrl()}/${encodeURIComponent(id_client)}/internal_remove/preview`;
    const response = await makeAuthenticatedRequest(url);
    return response;
  } catch (error) {
    console.error(`🌐 previewWorkspaceDeletion API error:`, error);
    throw new Error("Failing previewWorkspaceDeletion: " + error.message);
  }
};

export const deleteWorkspace = async ({ id_client, confirm }) => {
  try {
    const url = `${getApiUrl()}/${encodeURIComponent(id_client)}/internal_remove`;
    // `confirm` must echo id_client — the server-side confirmation token (400 otherwise).
    const response = await makeAuthenticatedRequest(url, {
      method: "DELETE",
      body: JSON.stringify({ confirm }),
    });
    if (import.meta.env.DEV) console.log("🚀 Workspace deletion started:", response);
    return response;
  } catch (error) {
    console.error(`🌐 deleteWorkspace API error:`, error);
    throw new Error("Failing deleteWorkspace: " + error.message);
  }
};

// Poll an async workspace-deletion job. Returns { jobId, id_client, status,
// steps, ... }; status is one of IN_PROGRESS | DONE | FAILED.
export const getDeletionJobStatus = async (jobId) => {
  try {
    const url = `${getApiUrl()}/admin/delete-jobs/${encodeURIComponent(jobId)}`;
    const response = await makeAuthenticatedRequest(url);
    return response;
  } catch (error) {
    console.error(`🌐 getDeletionJobStatus API error:`, error);
    throw new Error("Failing getDeletionJobStatus: " + error.message);
  }
};
